/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { RefObject } from 'preact';
import type { MutableRef } from 'preact/hooks';
import type { Cell } from '#UNSAFE_TableView';
import type { ColumnResizeInfo } from '../Table';

import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import { useColorScheme } from '#hooks/UNSAFE_useColorScheme';
import { useId } from '#hooks/UNSAFE_useId';
import { useScale } from '#hooks/UNSAFE_useScale';
import { TooltipContent } from '#hooks/UNSAFE_useTooltip/TooltipContent';
import {
  getGlobalTooltipManager,
  getWrappedTooltipContent,
  HIDE_TOOLTIP_DELAY,
  SHOW_TOOLTIP_DELAY
} from '#utils/PRIVATE_tooltipUtils';
import { getElementFromCell } from '../utils/TableDomUtils';
import { cellComparator } from '../utils/TableFocusUtils';
import { hasTruncatedText } from '../utils/TableTooltipUtils';

type UseTruncationTooltipOptions<C> = {
  rootRef: RefObject<HTMLDivElement>;
  tableId: string;
  resizeInfoRef: MutableRef<ColumnResizeInfo<C>>;
};

// delay small interaction times concerning a tooltip
const SMALL_INTERACTION_DELAY = 50;
const TABLE_HIDE_TOOLTIP_DELAY = Math.max(
  Math.min(SHOW_TOOLTIP_DELAY - SMALL_INTERACTION_DELAY, HIDE_TOOLTIP_DELAY),
  0
);

/**
 * Hook that manages cell template tooltip interactions on the Table when truncation is present.
 */
export function useTruncationTooltip<K, C>({
  rootRef,
  tableId,
  resizeInfoRef
}: UseTruncationTooltipOptions<C>) {
  const globalTooltipManager = getGlobalTooltipManager();

  const tooltipId = useId();

  const tooltipCellRef = useRef<Cell<K, C>>();
  const pendingShowTooltipCellRef = useRef<Cell<K, C>>();
  const tooltipAnchorRef = useRef<HTMLElement>();
  const tooltipTextRef = useRef<string>();
  const [tooltipText, setTooltipText] = useState<string>();

  const pointerDownRef = useRef<boolean>(false);
  const pointerDownTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const showTooltipTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const hideTooltipTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  // helper method for clearing the hide tooltip timeout
  const _clearHideTooltipTimeout = useCallback(() => {
    clearTimeout(hideTooltipTimeoutRef.current);
    hideTooltipTimeoutRef.current = undefined;
  }, []);

  // helper method for clearing the show tooltip timeout
  const _clearShowTooltipTimeout = useCallback(() => {
    clearTimeout(showTooltipTimeoutRef.current);
    showTooltipTimeoutRef.current = undefined;
    pendingShowTooltipCellRef.current = undefined;
  }, []);

  // helper method for setting up the show tooltip timeout
  const _setupShowTooltipTimeout = useCallback(() => {
    showTooltipTimeoutRef.current = setTimeout(function () {
      // when showing a tooltip, clear any pending 'clear tooltip timeouts'
      if (hideTooltipTimeoutRef.current != null) {
        _clearHideTooltipTimeout();
      }
      // only show a tooltip if not currently performing a column resize gesture
      if (!resizeInfoRef.current.isResizing) {
        showTooltipTimeoutRef.current = undefined;
        const cell = pendingShowTooltipCellRef.current;
        const rootElement = rootRef.current;
        if (rootElement != null && cell != null) {
          const cellElement = getElementFromCell(rootElement, tableId, cell);
          if (cellElement != null && hasTruncatedText(cellElement)) {
            setTooltipText(String(cellElement.innerText).trim());
            tooltipAnchorRef.current = cellElement;
            tooltipCellRef.current = cell;
          }
        }
      }
      pendingShowTooltipCellRef.current = undefined;
    }, SHOW_TOOLTIP_DELAY);
  }, [rootRef, tableId, resizeInfoRef, _clearHideTooltipTimeout]);

  // helper method for setting up the hide tooltip timeout
  const _setupHideTooltipTimeout = useCallback(() => {
    hideTooltipTimeoutRef.current = setTimeout(function () {
      // unlike in the showtooltip timeout logic, we do not want to clear
      // pending show tooltip timeouts when clearing a tooltip as it could
      // still be waiting to be shown in another location at this time
      hideTooltipTimeoutRef.current = undefined;
      setTooltipText(undefined);
      tooltipAnchorRef.current = undefined;
      tooltipCellRef.current = undefined;
    }, TABLE_HIDE_TOOLTIP_DELAY);
  }, []);

  // callback method to signal a tooltip is desired on the given cell
  const showTooltip = useCallback(
    (anchorCell: Cell<K, C>) => {
      if (cellComparator(tooltipCellRef.current, anchorCell)) {
        // if the tooltip is already showing in the right cell, clear all timeouts
        if (showTooltipTimeoutRef.current != null) {
          _clearShowTooltipTimeout();
        }
        if (hideTooltipTimeoutRef.current != null) {
          _clearHideTooltipTimeout();
        }
        return;
      }
      if (showTooltipTimeoutRef.current == null) {
        // if no tooltip is already pending, start a new tooltip timeout
        pendingShowTooltipCellRef.current = anchorCell;
        _setupShowTooltipTimeout();
      } else if (!cellComparator(pendingShowTooltipCellRef.current, anchorCell)) {
        // if a different tooltip is already pending, start a new tooltip timeout
        _clearShowTooltipTimeout();
        pendingShowTooltipCellRef.current = anchorCell;
        _setupShowTooltipTimeout();
      }
      if (tooltipCellRef.current != null && hideTooltipTimeoutRef.current == null) {
        // if the tooltip is showing on a different cell, ensure a hide timeout is setup
        _setupHideTooltipTimeout();
      }
    },
    [
      _setupShowTooltipTimeout,
      _setupHideTooltipTimeout,
      _clearShowTooltipTimeout,
      _clearHideTooltipTimeout
    ]
  );

  // callback method to signal the current tooltip is no longer desired
  const hideTooltip = useCallback(
    (isImmediate?: boolean) => {
      // ignore hide tooltip calls if due to focus loss from a pointer down on the tooltip itself
      if (pointerDownRef.current) {
        return;
      }
      if (showTooltipTimeoutRef.current != null) {
        _clearShowTooltipTimeout();
      }
      if (isImmediate) {
        if (hideTooltipTimeoutRef.current != null) {
          _clearHideTooltipTimeout();
        }
        setTooltipText(undefined);
        tooltipAnchorRef.current = undefined;
        tooltipCellRef.current = undefined;
      } else if (hideTooltipTimeoutRef.current == null) {
        _setupHideTooltipTimeout();
      }
    },
    [_setupHideTooltipTimeout, _clearShowTooltipTimeout, _clearHideTooltipTimeout]
  );

  // Pointer move handler for the tooltip.
  const _pointerMoveHandler = useCallback((_event: PointerEvent) => {
    if (showTooltipTimeoutRef.current != null) {
      clearTimeout(showTooltipTimeoutRef.current);
      showTooltipTimeoutRef.current = undefined;
      pendingShowTooltipCellRef.current = undefined;
    }
    if (hideTooltipTimeoutRef.current != null) {
      clearTimeout(hideTooltipTimeoutRef.current);
      hideTooltipTimeoutRef.current = undefined;
    }
  }, []);

  // Pointer leave handler for the tooltip.
  const _pointerLeaveHandler = useCallback(
    (event: PointerEvent) => {
      const targetElement = event.relatedTarget as HTMLElement;
      if (targetElement != null && targetElement.closest(`#${tableId}`) != null) {
        return;
      }
      hideTooltip();
    },
    [tableId, hideTooltip]
  );

  // Pointer down handler for the tooltip.
  const _pointerDownHandler = useCallback((_event: PointerEvent) => {
    pointerDownRef.current = true;
    if (pointerDownTimeoutRef.current != null) {
      clearTimeout(pointerDownTimeoutRef.current);
    }
    pointerDownTimeoutRef.current = setTimeout(function () {
      pointerDownRef.current = false;
      pointerDownTimeoutRef.current = undefined;
    }, SMALL_INTERACTION_DELAY);
  }, []);

  // handles logic involving competing tooltips managed outside of the TableView.
  // make sure this check runs BEFORE the rendered content is created below
  const isOpenOverrideRef = useRef<boolean>(true);
  if (tooltipText != null && tooltipText !== tooltipTextRef.current) {
    isOpenOverrideRef.current = true;
  }

  const tooltipPointerProps = {
    onPointerLeave: _pointerLeaveHandler,
    onPointerMove: _pointerMoveHandler,
    onPointerDown: _pointerDownHandler
  };

  // avoid applying undefined text after initial render completes as it can appear
  // while the tooltip is going through its closing animation and looks broken
  const renderRawContent = (
    <TooltipContent
      {...tooltipPointerProps}
      id={tooltipId}
      isOpen={isOpenOverrideRef.current}
      isDatatip={false}>
      {tooltipText != null ? tooltipText : tooltipTextRef.current}
    </TooltipContent>
  );

  // make sure these are in sync AFTER the rendered content is created above - this helps ensure
  // that empty text does not show up while the tooltip is going through a closing animation.
  tooltipTextRef.current = tooltipText;

  // offsets attempt to show the tooltip without overlapping content of nearby cells
  const scale = useScale();
  let mainAxisOffset = -8;
  let crossAxisOffset = -14;
  if (scale === 'md') {
    mainAxisOffset = -7;
    crossAxisOffset = -12;
  } else if (scale === 'sm') {
    mainAxisOffset = -6;
    crossAxisOffset = -11;
  }
  const currentColorScheme = useColorScheme();
  const tooltipContent =
    tooltipAnchorRef.current != null
      ? getWrappedTooltipContent(
          renderRawContent,
          'tooltip',
          currentColorScheme,
          tooltipAnchorRef as RefObject<HTMLElement>,
          'end-top',
          { mainAxis: mainAxisOffset, crossAxis: crossAxisOffset }
        )
      : undefined;

  const destroyCallback = useCallback(() => {
    hideTooltip(true);
  }, [hideTooltip]);

  useEffect(() => {
    if (isOpenOverrideRef.current && tooltipAnchorRef.current != null) {
      globalTooltipManager.register(destroyCallback);
    } else {
      globalTooltipManager.unregister(destroyCallback);
    }
  }, [tooltipAnchorRef, destroyCallback, tooltipText, globalTooltipManager]);

  return { tooltipContent, tooltipId, showTooltip, hideTooltip, tooltipAnchorRef };
}
